import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { api, ApiError } from '../../lib/api';
import Modal from '../ui/Modal';
import { useToast } from '../ui/Toast';
import { createWalletSchema, type CreateWalletFormData } from '../../schemas/wallet';

const WALLET_TYPES = ['cash', 'bank', 'crypto', 'payment_system'] as const;

export interface CreateWalletModalProps {
  entityId: string;
  currencies: Array<{ id: string; code: string; name: string; type: string }>;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateWalletModal({
  entityId,
  currencies,
  onClose,
  onSuccess,
}: CreateWalletModalProps) {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const toast = useToast();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateWalletFormData>({
    resolver: zodResolver(createWalletSchema) as any,
    defaultValues: {
      name: '',
      type: '' as CreateWalletFormData['type'],
      currencyCode: '',
    },
  });

  const selectedType = watch('type');
  const filteredCurrencies = selectedType
    ? currencies.filter((c: { type: string }) =>
        selectedType === 'crypto' ? c.type === 'crypto' : c.type === 'fiat'
      )
    : [];

  const handleTypeChange = () => {
    setValue('currencyCode', '');
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateWalletFormData) =>
      api.admin.wallets.create(entityId, {
        name: data.name,
        type: data.type,
        currencyCode: data.currencyCode,
        address: data.address || undefined,
        description: data.description || undefined,
      }),
    onSuccess,
    onError: (err: ApiError) => toast.error(err.message),
  });

  return (
    <Modal isOpen onClose={onClose} title={t('wallets.newWallet')} size="sm">
      <form onSubmit={handleSubmit((data: CreateWalletFormData) => createMutation.mutate(data))}>
        <div className="form-group">
          <label className="form-label">{t('wallets.fields.name')}</label>
          <input
            type="text"
            className={`form-input ${errors.name ? 'border-error' : ''}`}
            placeholder="Main Cash Register"
            {...register('name')}
          />
          {errors.name && (
            <span className="block text-error text-[13px] mt-1">{errors.name.message}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('wallets.fields.type')}</label>
          <select
            className={`form-input ${errors.type ? 'border-error' : ''}`}
            {...register('type', { onChange: handleTypeChange })}
          >
            <option value="" disabled>Select type...</option>
            {WALLET_TYPES.map((wt) => (
              <option key={wt} value={wt}>{t(`wallets.types.${wt}`)}</option>
            ))}
          </select>
          {errors.type && (
            <span className="block text-error text-[13px] mt-1">{errors.type.message}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('wallets.fields.currency')}</label>
          <select
            className={`form-input ${(errors as { currencyCode?: { message?: string } }).currencyCode ? 'border-error' : ''}`}
            {...register('currencyCode')}
            disabled={!selectedType}
          >
            <option value="" disabled>
              {!selectedType ? 'Select type first...' : 'Select currency...'}
            </option>
            {filteredCurrencies.map((c: { id: string; code: string; name: string }) => (
              <option key={c.id} value={c.code}>{c.code} - {c.name}</option>
            ))}
          </select>
          {selectedType && filteredCurrencies.length === 0 && (
            <span className="block text-foreground-muted text-xs mt-1 italic">
              No {selectedType === 'crypto' ? 'crypto' : 'fiat'} currencies available
            </span>
          )}
          {(errors as { currencyCode?: { message?: string } }).currencyCode && (
            <span className="block text-error text-[13px] mt-1">
              {(errors as { currencyCode?: { message?: string } }).currencyCode?.message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('wallets.fields.address')}</label>
          <input type="text" className="form-input" placeholder="Optional" {...register('address')} />
        </div>

        <div className="form-group">
          <label className="form-label">{t('wallets.fields.description')}</label>
          <textarea
            className="form-input"
            rows={2}
            placeholder="Optional notes"
            {...register('description')}
          />
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? tc('actions.loading') : tc('actions.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
